import React, {
  useCallback, useMemo, useRef,
  forwardRef, useImperativeHandle
} from 'react'
import PropTypes from 'prop-types'
import useVariableFilters from '../FiltersSidebar/hooks/useVariableFilters'
import useFilterVariableValues from '../FiltersSidebar/hooks/useFilterVariableValues'
import { formatVariableOptions } from '../FiltersSidebar/utils'
import { Checkbox } from '../ui'
import SummaryVariableFilters from './components/SummaryVariableFilters'

const CHECKBOX_LABEL_CLASS = 'tw:flex! tw:items-center tw:gap-2 tw:cursor-pointer tw:m-0!'
const CHECKBOX_TEXT_CLASS = 'tw:text-sm tw:text-gray-900'

const buildInitialFilters = summaryFilters => ({
  variableFilters: (summaryFilters || []).map(filter => ({
    id: filter.id,
    variableId: filter.variableId || filter.variable_id,
    values: filter.values || [],
    collectId: null
  }))
})

const SummaryFilters = forwardRef(({
  collectId, errors, initialFilters, variables, showPriorityResources,
  showCurrentDraftOnly, priorityResources, currentDraftOnly,
  onPriorityChange, onDraftOnlyChange
}, ref) => {
  const { variableValues, loadVariableValues } = useFilterVariableValues(collectId)
  const initial = useRef(buildInitialFilters(initialFilters)).current

  const {
    activeVariableFilters,
    variableFilters,
    addVariableFilter,
    removeVariableFilter,
    updateVariableFilter,
    updateVariableValues
  } = useVariableFilters(initial, collectId, loadVariableValues)

  /* eslint-disable camelcase */
  useImperativeHandle(ref, () => ({
    getFilters: () => variableFilters.map(filter => ({
      id: filter.id,
      variable_id: filter.variableId,
      values: filter.values || [],
      _destroy: Boolean(filter.shouldDestroy)
    }))
  }), [variableFilters])
  /* eslint-enable camelcase */

  const variableObjects = useMemo(
    () => variables.map(([name, id]) => ({ id, name })), [variables]
  )

  const usedIds = activeVariableFilters.map(
    filter => filter.variableId?.toString()
  )
  const variableOptions = useMemo(
    () => formatVariableOptions(variableObjects, usedIds), [variableObjects, activeVariableFilters]
  )

  const getValueOptions = useCallback(variableId => {
    if (!variableId)
      return []

    return variableValues[variableId] || []
  }, [variableValues])

  return (
    <div className="mt-3">
      <h5 className="font-weight-normal">Filtres</h5>
      {showPriorityResources ? (
        <label className={CHECKBOX_LABEL_CLASS}>
          <Checkbox checked={priorityResources} onChange={onPriorityChange} />
          <span className={CHECKBOX_TEXT_CLASS}>Limiter aux ressources prioritaires</span>
        </label>
      ) : null}
      {showCurrentDraftOnly ? (
        <label className={CHECKBOX_LABEL_CLASS}>
          <Checkbox checked={currentDraftOnly} onChange={onDraftOnlyChange} />
          <span className={CHECKBOX_TEXT_CLASS}>
            Limiter aux réponses de la planification en cours
          </span>
        </label>
      ) : null}
      <SummaryVariableFilters
        activeFilters={activeVariableFilters}
        errors={errors}
        variableOptions={variableOptions}
        valueOptions={getValueOptions}
        collect={{ variables: variableObjects }}
        onAddFilter={addVariableFilter}
        onRemoveFilter={removeVariableFilter}
        onVariableChange={updateVariableFilter}
        onValuesChange={updateVariableValues}
      />
    </div>
  )
})

SummaryFilters.displayName = 'SummaryFilters'

SummaryFilters.propTypes = {
  collectId: PropTypes.number.isRequired,
  currentDraftOnly: PropTypes.bool,
  initialFilters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    values: PropTypes.arrayOf(PropTypes.string),
    variableId: PropTypes.number
  })).isRequired,
  onDraftOnlyChange: PropTypes.func,
  onPriorityChange: PropTypes.func,
  priorityResources: PropTypes.bool,
  showCurrentDraftOnly: PropTypes.bool,
  showPriorityResources: PropTypes.bool,
  variables: PropTypes.arrayOf(PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  )).isRequired
}

SummaryFilters.defaultProps = {
  currentDraftOnly: false,
  onDraftOnlyChange: null,
  onPriorityChange: null,
  priorityResources: false,
  showCurrentDraftOnly: false,
  showPriorityResources: false
}

export default SummaryFilters
